import { useEffect, useMemo, useRef, useState } from 'react';
import {
  EMITTER_CONSTANTS,
  EVENT_COUNT_CONVERT,
  KEY_RECOIL,
  STRIP_EVENT_THUMBNAIL_INFO,
  TYPE_RHYTHM_EVENT_ENUM,
  TYPE_SPECIAL_EVENT_ENUM,
} from '@/shared/config';
import { useEmitter, usePaginationKeyboard } from '@/shared/lib/customHooks';
import { createOnChangePageString } from '@/shared/lib/parsePageInput';
import {
  RHYTHM_EVENT_TAB_ID,
  selectEventFilter,
  selectIsOpenModalEditStrip,
  selectPageIndex,
  selectSpecialEventCount,
  useReportStore,
} from '@/entities/report';
import { cancelAllAPI } from '@/shared/lib/apiHelpers';
import { useEventsInfoRhythmEvents, isAppliedFilterEvent } from '@/entities/event';
import type { PaginationContainerProps } from './paginationContainer';

/**
 * Drives the strip pagination of the rhythm events tab, taking its total from the filtered special-event count or from the tab's own event counts. It syncs a local page string and input width with the store page index, validates typed pages on Enter and blur, cancels in-flight calls when stepping, binds Z and X while the tab area is hovered unless a strip edit modal is open, and listens to the rhythm event next, previous and edit-modal emitter events.
 *
 * @param props - Active rhythm event type, the per-type special event selections and the disabled flag.
 * @returns An object carrying the pagination flags, page string, page bounds, input size, error flag and the previous, next, change, key-down and blur handlers.
 */
const usePaginationContainer = ({
  activeButton,
  specialEventTypes,
  isDisabled,
}: PaginationContainerProps) => {
  const keyRecoil = KEY_RECOIL.RHYTHM_EVENT;

  const pageIndex = useReportStore(selectPageIndex(keyRecoil));
  const setPageIndexAction = useReportStore((s) => s.actions.setPageIndex);
  /**
   * Writes the rhythm events tab's strip page index. Passes the TAB_2 key with the new page index or updater to the store's setPageIndex action.
   *
   * @param v - The next page index, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setPageIndex = (v: Parameters<typeof setPageIndexAction>[1]) =>
    setPageIndexAction(keyRecoil, v);
  const [pageString, setPageString] = useState(pageIndex.index + 1);
  const [inputSize, setInputSize] = useState(String(pageIndex.index + 1).length + 1);
  const setIsRenderViewerEcg = useReportStore((s) => s.actions.setIsRenderViewerEcg);
  const [isError, setIsError] = useState(false);
  const isOpenModalEditStrip = useReportStore(selectIsOpenModalEditStrip(keyRecoil));
  const specialEventCount = useReportStore(selectSpecialEventCount(keyRecoil));
  const eventsInfo = useEventsInfoRhythmEvents();
  const eventFilter = useReportStore(selectEventFilter(keyRecoil));

  const [isOpenModaltrip, setIsOpenModalStrip] = useState(false);

  const pageStringRef = useRef(pageString);
  const maxPageNumberRef = useRef<number>();

  const totalStrip = useMemo(() => {
    if (
      isAppliedFilterEvent(eventFilter, activeButton) ||
      ([
        TYPE_RHYTHM_EVENT_ENUM.TACHY,
        TYPE_RHYTHM_EVENT_ENUM.BRADY,
        TYPE_RHYTHM_EVENT_ENUM.PAUSE,
      ].includes(activeButton) &&
        specialEventTypes[activeButton] &&
        specialEventTypes[activeButton] !== TYPE_SPECIAL_EVENT_ENUM.ALL_HR_RANGES)
    ) {
      return (specialEventCount as any)?.[(EVENT_COUNT_CONVERT as any)[activeButton]]?.count;
    }
    return (eventsInfo as any)[(EVENT_COUNT_CONVERT as any)[activeButton]]?.count;
  }, [eventFilter, specialEventCount, activeButton, specialEventTypes, eventsInfo]);

  const maxPageNumber = totalStrip
    ? Math.ceil(totalStrip / STRIP_EVENT_THUMBNAIL_INFO.stripDisplayLimit)
    : 1;
  maxPageNumberRef.current = maxPageNumber;
  // *: Always display pagination
  // const isShowPagination = totalStrip > stripDisplayLimit;
  const isShowPagination = true;
  const isDisabledNext =
    (pageIndex.index + 1) * STRIP_EVENT_THUMBNAIL_INFO.stripDisplayLimit >= totalStrip;
  const isDisabledPrev = pageIndex.index === 0;

  /**
   * Steps back the rhythm events strip pagination by one page. Cancels in-flight API calls, decrements the store page index and marks the ECG viewer for re-render.
   *
   * @returns Nothing.
   */
  const onClickPrevious = () => {
    cancelAllAPI();
    setPageIndex({ index: pageIndex.index - 1 });
    setIsRenderViewerEcg(true);
  };

  /**
   * Advances the rhythm events strip pagination by one page. Cancels in-flight API calls, increments the store page index and marks the ECG viewer for re-render.
   *
   * @returns Nothing.
   */
  const onClickNext = () => {
    cancelAllAPI();
    setPageIndex({ index: pageIndex.index + 1 });
    setIsRenderViewerEcg(true);
  };

  const onChangePageString = createOnChangePageString({
    setIsError,
    setPageString,
    pageStringRef,
    setInputSize,
    setIsRenderViewerEcg,
  });

  /**
   * Commits the typed page number of the rhythm events pagination when the input loses focus. Accepts the value when it parses and stays within the page count, moving the store page index only if it actually changed, otherwise raises the error flag.
   *
   * @param e - The blur event, unused.
   * @returns Nothing.
   */
  const onBlur = (e: any) => {
    if (pageString) {
      const pageNumber = Number(pageString);
      if (pageNumber && pageNumber <= maxPageNumber) {
        const newPageIndex = Number(pageString) - 1;
        setPageIndex((prev) => (newPageIndex !== prev.index ? { index: newPageIndex } : prev));
      } else {
        setIsError(true);
      }
    }
  };

  const { onKeyDown } = usePaginationKeyboard({
    tabZoneId: RHYTHM_EVENT_TAB_ID,
    isDisabled,
    isBlocked: isOpenModalEditStrip || isOpenModaltrip,
    isShowPagination,
    isDisabledPrev,
    isDisabledNext,
    onClickPrevious,
    onClickNext,
    pageStringRef,
    maxPageNumberRef,
    setPageIndex,
    setIsError,
    deps: [totalStrip, pageIndex, isDisabled, isOpenModaltrip, isOpenModalEditStrip],
  });

  useEmitter(
    EMITTER_CONSTANTS.RHYTHM_EVENTS_NEXT,
    () => {
      if (isShowPagination && !isDisabledNext) {
        onClickNext();
      }
    },
    [pageIndex, totalStrip],
  );

  useEmitter(
    EMITTER_CONSTANTS.RHYTHM_EVENTS_PREV,
    () => {
      if (isShowPagination && !isDisabledPrev) {
        onClickPrevious();
      }
    },
    [pageIndex, totalStrip],
  );

  useEmitter(
    EMITTER_CONSTANTS.OPEN_MODAL_EDIT_AI_STRIP,
    (msg: any) => {
      setIsOpenModalStrip(msg.value);
    },
    [],
  );

  useEffect(() => {
    setPageString(pageIndex.index + 1);
    setInputSize(String(pageIndex.index + 1).length + 1);
  }, [pageIndex]);

  useEffect(() => {
    if (maxPageNumber <= pageIndex.index) {
      setPageIndex({ index: maxPageNumber - 1 });
    }
  }, [maxPageNumber, pageIndex]);

  return {
    isShowPagination,
    isDisabledPrev,
    isDisabledNext,
    pageString,
    maxPageNumber,
    inputSize,
    isError,
    onClickPrevious,
    onClickNext,
    onChangePageString,
    onKeyDown,
    onBlur,
  };
};

export default usePaginationContainer;
